/**
 * Guarda Mundo - Collection models carousel: setas + scroll por página.
 * Esconde setas quando todos os slides cabem no viewport (sem overflow).
 */
import { scrollIntoView } from '@theme/scrolling';

if (!customElements.get('gm-collection-models-carousel')) {
  class GmCollectionModelsCarousel extends HTMLElement {
    connectedCallback() {
      this._viewport = this.querySelector('[data-gm-models-viewport]');
      this._track = this.querySelector('[data-gm-models-track]');
      this._slides = [...this.querySelectorAll('.gm-collection-category__models-card')];
      this._prevBtn = this.querySelector('[data-gm-models-prev]');
      this._nextBtn = this.querySelector('[data-gm-models-next]');

      if (!this._viewport || !this._track || this._slides.length === 0) return;

      this._pageIndex = 0;
      this._refreshMetrics();

      this._onScroll = () => {
        if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
        this._scrollRaf = requestAnimationFrame(() => this._syncFromScroll());
      };

      this._viewport.addEventListener('scroll', this._onScroll, { passive: true });
      this._prevBtn?.addEventListener('click', () => this._goToPage(this._pageIndex - 1));
      this._nextBtn?.addEventListener('click', () => this._goToPage(this._pageIndex + 1));

      this._resizeObserver = new ResizeObserver(() => {
        this._refreshMetrics();
        this._syncFromScroll();
      });
      this._resizeObserver.observe(this._viewport);
      this._resizeObserver.observe(this._track);

      this._syncButtons();
    }

    disconnectedCallback() {
      this._viewport?.removeEventListener('scroll', this._onScroll);
      this._resizeObserver?.disconnect();
      if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
    }

    _readVisibleCount() {
      return (
        Number.parseInt(
          getComputedStyle(this).getPropertyValue('--gm-models-visible').trim(),
          10
        ) || 3
      );
    }

    _refreshMetrics() {
      this._visibleCount = this._readVisibleCount();
      this._pageCount = Math.max(1, Math.ceil(this._slides.length / this._visibleCount));

      // Overflow real: se o track cabe no viewport, não precisa de setas.
      const overflow =
        this._viewport.scrollWidth > this._viewport.clientWidth + 1 ||
        this._pageCount > 1;
      this.dataset.scrollable = overflow ? 'true' : 'false';
    }

    _getPageWidth() {
      return this._viewport.clientWidth;
    }

    _syncFromScroll() {
      this._refreshMetrics();
      if (this.dataset.scrollable !== 'true') {
        this._setPage(0);
        return;
      }

      const pageWidth = this._getPageWidth();
      if (pageWidth <= 0) return;

      let index = Math.round(this._viewport.scrollLeft / pageWidth);
      index = Math.max(0, Math.min(index, this._pageCount - 1));
      this._setPage(index);
    }

    _setPage(index) {
      const lastPage = this._pageCount - 1;
      index = Math.max(0, Math.min(index, lastPage));

      const scrollable = this.dataset.scrollable === 'true';
      const prevDisabled = !scrollable || index === 0;
      const nextDisabled = !scrollable || index === lastPage;

      if (
        index === this._pageIndex &&
        this._prevBtn?.disabled === prevDisabled &&
        this._nextBtn?.disabled === nextDisabled
      ) {
        return;
      }

      this._pageIndex = index;

      if (this._prevBtn) this._prevBtn.disabled = prevDisabled;
      if (this._nextBtn) this._nextBtn.disabled = nextDisabled;
    }

    _syncButtons() {
      this._refreshMetrics();
      this._setPage(this._pageIndex);
    }

    _goToPage(index) {
      if (this.dataset.scrollable !== 'true') return;

      const targetSlide = this._slides[index * this._visibleCount];
      if (!targetSlide) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      scrollIntoView(targetSlide, {
        ancestor: this._viewport,
        inline: 'start',
        block: 'nearest',
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
      this._setPage(index);
    }
  }

  customElements.define('gm-collection-models-carousel', GmCollectionModelsCarousel);
}
